import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';

import Tooltip from 'common/deps/uiv/Tooltip';

const ERROR_MESSAGES = {
  typeMismatch: el => {
    if (el.type === 'email')
      return 'Ingrese una dirección de correo electrónico válida';
    if (el.type === 'url')
      return 'Ingrese una dirección de correo electrónico válida';
    return 'Ingrese un formato válido';
  },
  badInput: el => {
    if (el.type === 'number') return 'Ingrese un número válido';
    return 'Ingrese un formato válido';
  },
  tooShort: el =>
    `Debe contener al menos ${el.getAttribute('minlength')} caracteres`,
  tooLong: el =>
    `Debe contener como máximo ${el.getAttribute('minlength')} caracteres`,
  rangeUnderflow: el => `Debe ser mayor o igual a ${el.getAttribute('min')}`,
  rangeOverflow: el => `Debe ser menor o igual a ${el.getAttribute('max')}`,
  stepMismatch: el => {
    if (!el.step) return 'Ingrese un número entero';
    return `Ingrese un número múltiple de ${el.step}`;
  },
  patternMismatch: () => 'Ingrese un formato válido',
  valueMissing: () => 'Este campo es obligatorio',
};

const DEFAULT_ERROR_MESSAGE = 'Este campo es inválido';

const FIELD_ERROR_CLASS = '--danger';

@Component({
  components: {
    Tooltip,
  },
  directives: {
    tooltip: Tooltip,
  },
  props: {
    names: {
      type: Array,
      default: () => [],
    },
    tag: {
      type: String,
      default: 'div',
    },
    tooltipPlacement: {
      type: String,
      default: 'bottom',
    },
    errorMessages: {
      type: Object,
      default: undefined,
    },
    value: {
      required: true,
    },
    customError: {
      type: String,
      required: false,
    },
  },
})
export default class BbFieldComponent extends Vue {
  bbFieldComponent = true; // Identify BbField from BbForm
  fieldEls = null;
  errors = {};
  isFocused = false;
  isValid = false;
  wasFormSubmitted = false;
  wasFocused = false;
  hasHover = false;
  customErrors = null;
  lastTooltipMessage = '';
  errorClass = FIELD_ERROR_CLASS;

  constructor() {
    super();
    this.validate = this.validate.bind(this);
  }

  get errorMessage() {
    if (this.isValid) return '';
    if (this.customErrors) return this.customErrors[0];
    const errorEntry = Object.entries(this.errors).find(([, v]) => v);
    if (!errorEntry) return DEFAULT_ERROR_MESSAGE;
    const errorKey = errorEntry[0];
    const errorMsgFn = this.errorMessagesMap[errorKey];
    if (!errorMsgFn) return DEFAULT_ERROR_MESSAGE;
    return errorMsgFn(this.fieldEl);
  }

  get tooltipMessage() {
    if (this.isErrorDisplayed && this.customError === '') {
      return this.errorMessage;
    }
    return this.customError;
  }

  get isErrorDisplayed() {
    return (this.wasFocused || this.wasFormSubmitted) && !this.isValid;
  }

  get showTooltip() {
    return this.isFocused || this.hasHover || this.isErrorDisplayed;
  }

  set showTooltip(value) {
    // noop
  }

  /**
   * Id is because MP dosen't want name in the input attribute.
   * @returns {*[]}
   */
  get fieldNames() {
    if (this.fieldEl.id === '') {
      return [this.fieldEl.name].concat(this.names);
    }
    return [this.fieldEl.id].concat(this.names);
  }

  get fieldEl() {
    return this.fieldEls[0];
  }

  get errorMessagesMap() {
    return Object.assign({}, ERROR_MESSAGES, this.errorMessages);
  }

  @Watch('tooltipMessage')
  tooltipMessageChanged() {
    if (this.tooltipMessage) {
      this.lastTooltipMessage = this.tooltipMessage;
    }
  }

  @Watch('isErrorDisplayed')
  errorDisplayedChanged() {
    const method = this.isErrorDisplayed ? 'add' : 'remove';
    this.fieldEls.forEach(field => {
      field.classList[method](this.errorClass);
    });
  }

  @Watch('value')
  valueChanged() {
    setTimeout(() => {
      this.validate();
    });
  }

  mounted() {
    this.$el.bbFieldCtrl = this;
    this.fieldEls = Array.from(
      this.$el.querySelectorAll('input,select,textarea'),
    );
    this.fieldEls.forEach(field => {
      // field.addEventListener('change', this.validate);
      // field.addEventListener('input', this.validate);
      field.addEventListener('focus', () => {
        this.isFocused = true;
      });
      field.addEventListener('blur', () => {
        this.isFocused = false;
        if (!field.validity.valueMissing) {
          this.wasFocused = true;
        }
      });
    });
    this.$el.addEventListener('mouseover', () => {
      this.hasHover = true;
    });
    this.$el.addEventListener('mouseleave', () => {
      this.hasHover = false;
    });
    this.validate();
  }

  validate() {
    this.customErrors = null;
    this.fieldEl.setCustomValidity('');
    this.isValid = this.fieldEl.checkValidity();
    const { validity } = this.fieldEl;
    this.errors = Object.keys(ERROR_MESSAGES).reduce((errors, key) => {
      errors[key] = validity[key];
      return errors;
    }, {});
    this.fieldEl.setCustomValidity(this.errorMessage);
  }

  formSubmitted() {
    this.wasFormSubmitted = true;
    this.validate();
  }

  setErrors(errors) {
    if (errors && errors.length === 0) errors = null;
    this.customErrors = errors;
    if (errors) {
      this.isValid = false;
      this.fieldEl.setCustomValidity(errors[0]);
    }
  }

  focus() {
    this.fieldEl.focus();
  }
}
